import React, { useState, useContext, useEffect } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";

import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import AccountCircle from "@material-ui/icons/AccountCircle";
import CachedIcon from "@material-ui/icons/Cached";
import InboxIcon from "@material-ui/icons/Inbox";
import Brightness4Icon from '@material-ui/icons/Brightness4';
import Brightness7Icon from '@material-ui/icons/Brightness7';

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import NotificationsVolume from "../components/NotificationsVolume";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import ChatPopover from "../pages/Chat/ChatPopover";
import { useDate } from "../hooks/useDate";
import ColorModeContext from "../layout/themeContext";
import { SocketContext } from "../context/Socket/SocketContext";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    [theme.breakpoints.down("sm")]: {
      height: "calc(100vh - 56px)",
    },
    backgroundColor: theme.palette.fancyBackground,
    '& .MuiButton-outlinedPrimary': {
      color: theme.palette.primary.main,
      border: `1px solid ${theme.palette.primary.main}`,
    },
    '& .MuiTab-textColorPrimary.Mui-selected': {
      color: theme.palette.primary.main,
    }
  },
  toolbar: {
    paddingRight: 24,
    color: theme.palette.dark.main,
    background: theme.palette.barraSuperior,
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 8px",
    minHeight: "48px",
    [theme.breakpoints.down("sm")]: {
      height: "48px"
    }
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      display: "none"
    }
  },
  menuButton: {
    marginRight: 36,
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    flexGrow: 1,
    fontSize: 14,
    color: "white",
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
    ...theme.scrollbarStylesSoft
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    }
  },
  content: {
    flexGrow: 1,
    overflow: "auto",
    paddingTop: theme.spacing(6),
  },
  containerWithScroll: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  logo: {
    width: "80%",
    height: "auto",
    maxWidth: 180,
    marginTop: 7,
    marginBottom: 10,
    [theme.breakpoints.down("sm")]: {
      width: "auto",
      height: "80%",
      maxWidth: 180,
      marginTop: 7,
      marginBottom: 10,
    },
    logo: theme.logo
  },
  rightDrawerIcon: {
    color: theme.palette.text.primary,
    "& svg": {
      color: theme.palette.type === 'dark' ? '#fff !important' : `${theme.palette.text.primary} !important`,
    },
    cursor: "pointer",
    "&:hover": {
      opacity: 0.8
    }
  },
  listItem: {
    padding: theme.spacing(2),
  },
  iconWrapper: {
    "& > *": {
      color: theme.palette.type === 'dark' ? '#fff !important' : `${theme.palette.text.primary} !important`,
    },
    "& svg": {
      color: theme.palette.type === 'dark' ? '#fff !important' : `${theme.palette.text.primary} !important`,
    },
    "& button": {
      color: theme.palette.type === 'dark' ? '#fff !important' : `${theme.palette.text.primary} !important`,
    }
  }
}));

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading, user } = useContext(AuthContext);
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(() => {
    const storedDrawerState = localStorage.getItem("drawerOpen");
    return storedDrawerState === null ? false : storedDrawerState === "true";
  });
  const [drawerVariant, setDrawerVariant] = useState("permanent");

  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("sm"));

  const [volume, setVolume] = useState(localStorage.getItem("volume") || 1);

  const socketManager = useContext(SocketContext);
  const { dateToClient } = useDate();

  useEffect(() => {
    if (document.body.offsetWidth > 600) {
      setDrawerOpen(true);
    }
  }, []);

  useEffect(() => {
    if (document.body.offsetWidth < 600) {
      setDrawerVariant("temporary");
    } else {
      setDrawerVariant("permanent");
    }
  }, [drawerOpen]);

  useEffect(() => {
    localStorage.setItem("drawerOpen", drawerOpen);
  }, [drawerOpen]);

  useEffect(() => {
    const userId = user.id;
    const socket = socketManager.getSocket(user.companyId);

    const onCompanyUserAuth = (data) => {
      if (data.user.id === +userId) {
        toastError("Sua conta foi acessada em outro computador.");
        setTimeout(() => {
          localStorage.clear();
          window.location.reload();
        }, 1000);
      }
    };
    socket.on(`company-${user.companyId}-auth`, onCompanyUserAuth);

    socket.emit("userStatus");
    const interval = setInterval(() => {
      socket.emit("userStatus");
    }, 1000 * 60 * 5);

    return () => {
      socket.off(`company-${user.companyId}-auth`, onCompanyUserAuth);
      clearInterval(interval);
    };
  }, [socketManager, user]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      setDrawerOpen(false);
    }
  };

  const handleRefreshPage = () => {
    window.location.reload(false);
  };

  const toggleColorMode = () => {
    colorMode.toggleColorMode();
  };

  if (loading) {
    return <BackdropLoading />;
  }

  const logo = `${process.env.REACT_APP_BACKEND_URL}/public/logotipos/interno.png`;

  return (
    <div className={classes.root}>
      <Drawer
        variant={drawerVariant}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose
          ),
        }}
        open={drawerOpen}
      >
        <div className={classes.toolbarIcon}>
          <img src={logo} className={classes.logo} alt="Logo" />
          <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
            <ChevronLeftIcon />
          </IconButton>
        </div>
        <Divider />
        <List className={classes.containerWithScroll}>
          <MainListItems drawerClose={drawerClose} collapsed={!drawerOpen} />
        </List>
        <Divider />
      </Drawer>

      <Drawer
        anchor="right"
        open={rightDrawerOpen}
        onClose={() => setRightDrawerOpen(false)}
      >
        <div style={{ width: 270 }}>
          <List>
            <ListItem className={classes.listItem}>
              <ListItemIcon>
                <IconButton 
                  className={classes.rightDrawerIcon}
                  onClick={() => {
                    handleRefreshPage();
                    setRightDrawerOpen(false);
                  }}
                >
                  <CachedIcon />
                </IconButton>
              </ListItemIcon>
              <ListItemText primary="Atualizar Página" />
            </ListItem>
            <Divider />

            <ListItem className={classes.listItem}>
              <ListItemIcon>
                <IconButton 
                  className={classes.rightDrawerIcon}
                  onClick={() => {
                    toggleColorMode();
                    setRightDrawerOpen(false);
                  }}
                >
                  {theme.palette.type === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                </IconButton>
              </ListItemIcon>
              <ListItemText primary={theme.palette.type === 'dark' ? "Modo claro" : "Modo escuro"} />
            </ListItem>
            <Divider />

            <ListItem className={classes.listItem}>
              <ListItemIcon className={classes.iconWrapper}>
                <NotificationsVolume
                  setVolume={setVolume}
                  volume={volume}
                />
              </ListItemIcon>
              <ListItemText primary="Volume de Notificação" />
            </ListItem>
            <Divider />

            <ListItem className={classes.listItem}>
              <ListItemIcon className={classes.iconWrapper}>
                <AnnouncementsPopover />
              </ListItemIcon>
              <ListItemText primary="Anúncios" />
            </ListItem>
            <Divider />

            <ListItem className={classes.listItem}>
              <ListItemIcon className={classes.iconWrapper}>
                <ChatPopover />
              </ListItemIcon>
              <ListItemText primary="Chat Interno" />
            </ListItem>
          </List>
        </div>
      </Drawer>

      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />

      <AppBar
        position="absolute"
        className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
        color="primary"
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          <IconButton
            edge="start"
            variant="contained"
            aria-label="open drawer"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className={clsx(
              classes.menuButton,
              drawerOpen && classes.menuButtonHidden
            )}
            style={{ color: '#FFF' }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            component="h2"
            variant="h6"
            color="inherit"
            noWrap
            className={classes.title}
          >
            {greaterThenSm && user?.profile === "admin" && user?.company?.dueDate ? (
              <>
                Olá <b>{user.name}</b>, Bem vindo a <b>{user?.company?.name}</b>! (Ativo até {dateToClient(user?.company?.dueDate)})
              </>
            ) : (
              <>
                Olá <b>{user.name}</b>, Bem Vindo a <b>{user?.company?.name}</b>!
              </>
            )}
          </Typography>

          {user.id && <NotificationsPopOver volume={volume} />}

          <IconButton
            edge="end"
            aria-label="open right drawer"
            onClick={() => setRightDrawerOpen(true)}
            style={{ color: "#FFF", marginRight: 8 }}
          >
            <InboxIcon />
          </IconButton>

          <div>
            <IconButton
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              style={{ color: "white" }}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleOpenUserModal}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <MenuItem onClick={handleClickLogout}>
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>

      <main className={classes.content}>
        <div style={{ minHeight: "0px" }} />
        {children ? children : null}
      </main>
    </div>
  );
};

export default LoggedInLayout;